import { useLayoutEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { selectSectorSpecificStyles, setSectorSpecificStyles } from 'client/store/layersSlice/layersSlice';
import { selectSelectedOwnerId } from 'client/store/isolatedViewsSlice/isolatedViewsSlice';
import { isRasterOwner } from '../RasterFeatureLoader';
import { ALL_CATEGORIES } from '../constants';
import useAssetOwnerCategories from './useAssetOwnerCategories';
import useAssetOwnerFiltering from './useAssetOwnerFiltering';

const useAssetOwnerIsolationState = ({ owner, sectorSymbology }) => {
    const dispatch = useDispatch();
    const sensitiveAssets = useSelector(state => state.assets.sensitiveAssets || [], shallowEqual);
    const selectedOwner = useSelector(selectSelectedOwnerId);
    const isActiveOwner = selectedOwner === owner?.dataProviderId;
    const hierarchy = useSelector(state => state?.layers?.result?.hierarchy);
    const mapLayers = useSelector(state => state?.mapConfig?.result?.layers);
    const allAssets = useSelector(state => state?.assets?.layers || {});
    const currentSectorSpecificStyles = useSelector(selectSectorSpecificStyles);

    const [selectedAssetCategory, setSelectedAssetCategory] = useState(ALL_CATEGORIES);

    const {
        categoriesForOwner,
        onlyHasOneCategory,
        hideAllOption,
        effectiveSelectedAssetCategory
    } = useAssetOwnerCategories({
        owner,
        sensitiveAssets,
        hierarchy,
        mapLayers,
        allAssets,
        selectedAssetCategory
    });

    const {
        filterAssetsById,
        handleSelectAll,
        handleChange,
        handleAccordionHeading,
        showRasterLayers
    } = useAssetOwnerFiltering({
        owner,
        hierarchy,
        selectedOwner,
        setSelectedAssetCategory,
        categoriesForOwner,
        effectiveSelectedAssetCategory
    });

    useLayoutEffect(() => {
        if (!isActiveOwner) {
            return;
        }

        showRasterLayers(isRasterOwner(owner.dataProviderId));
    }, [isActiveOwner, owner?.dataProviderId, selectedOwner, showRasterLayers]);

    useLayoutEffect(() => {
        if (!isActiveOwner) {
            return;
        }

        const enabledSector = Object.entries(sectorSymbology).find(([sector, enabled]) => {
            return enabled && effectiveSelectedAssetCategory?.toLowerCase() === sector.toLowerCase();
        })?.[0];

        const nextSectorSpecificStyles = {
            enabled: !!enabledSector,
            sector: enabledSector || null
        };

        // this prevents unnecessary dispatches when the computed state already matches redux
        if (
            currentSectorSpecificStyles?.enabled === nextSectorSpecificStyles.enabled &&
            currentSectorSpecificStyles?.sector === nextSectorSpecificStyles.sector
        ) {
            return;
        }

        dispatch(setSectorSpecificStyles(nextSectorSpecificStyles));
    }, [currentSectorSpecificStyles?.enabled, currentSectorSpecificStyles?.sector, dispatch, effectiveSelectedAssetCategory, isActiveOwner, sectorSymbology]);

    useLayoutEffect(() => {
        if (!isActiveOwner) {
            return;
        }

        if (effectiveSelectedAssetCategory === ALL_CATEGORIES) {
            filterAssetsById(owner?.dataProviderId, undefined, ALL_CATEGORIES);
            return;
        }

        const selectedCategoryObj = categoriesForOwner.find(category => category.id === effectiveSelectedAssetCategory);
        if (selectedCategoryObj) {
            handleChange(selectedCategoryObj);
        }
    }, [categoriesForOwner, effectiveSelectedAssetCategory, filterAssetsById, handleChange, isActiveOwner, owner?.dataProviderId, selectedOwner]);

    return {
        categoriesForOwner,
        selectedAssetCategory,
        setSelectedAssetCategory,
        effectiveSelectedAssetCategory,
        onlyHasOneCategory,
        hideAllOption,
        handleSelectAll,
        handleChange,
        filterAssetsById,
        showRasterLayers,
        handleAccordionHeading,
        selectedOwner
    };
};

export default useAssetOwnerIsolationState;
